"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const TabsIdContext = React.createContext<string | undefined>(undefined);

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ id, ...props }, ref) => {
  const generatedId = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const tabsId = id || `tabs-${generatedId}`;

  return (
    <TabsIdContext.Provider value={tabsId}>
      <TabsPrimitive.Root ref={ref} id={tabsId} {...props} />
    </TabsIdContext.Provider>
  );
});
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-12 items-center justify-center rounded py-1 text-primary w-full rounded-tl-xl rounded-tr-xl",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const tabsId = React.useContext(TabsIdContext);
  const idPrefix = tabsId ? `${tabsId}-` : "";

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "bg-putty-100 hover:bg-card-gray-hover inline-flex grow items-center justify-center whitespace-nowrap rounded p-3 text-sm font-bold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-secondary data-[state=active]:text-primary-foreground data-[state=active]:shadow data-[state=active]:hover:bg-secondary rounded-none first:rounded-tl-xl last:rounded-tr-xl",
        className,
      )}
      {...props}
      id={props.id || `${idPrefix}tab-trigger-${props.value}`}
      aria-controls={
        props["aria-controls"] || `${idPrefix}tab-content-${props.value}`
      }
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => {
  const tabsId = React.useContext(TabsIdContext);
  const idPrefix = tabsId ? `${tabsId}-` : "";

  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      {...props}
      id={props.id || `${idPrefix}tab-content-${props.value}`}
      aria-labelledby={
        props["aria-labelledby"] || `${idPrefix}tab-trigger-${props.value}`
      }
    />
  );
});
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
